import { useMapStore } from '@/store/remapStore';
import type { MapContext } from '@/store/remapStore/remapStore';
import type { Router, RouteLocationNormalizedLoaded } from 'vue-router';

export interface EntityGroup {
  entityId: string;
  entityType: string;
  entityTitle: string;
}

export interface EntityNavigationContext {
  context: MapContext | null;
  itemId: string;
  taskId?: string;
  taskType?: string;
}

/**
 * Resolve navigation context for opening entities in the map sidebar.
 *
 * Special cases:
 * - Child entities (Action/CC): Opens parent Activity with task selected
 * - Unsupported types: Returns null context
 */
export function resolveEntityNavigationContext(
  entityType: string,
  entityId: string,
  relatedEntityType: string | null,
  relatedEntityId: string | null,
): EntityNavigationContext {
  // Handle child entities - open parent Activity with task selected
  if (
    entityType === 'Action' &&
    relatedEntityType === 'Activity' &&
    relatedEntityId
  ) {
    return {
      context: 'activity',
      itemId: relatedEntityId,
      taskId: entityId,
      taskType: 'action',
    };
  }

  if (
    entityType === 'CompletionCriterion' &&
    relatedEntityType === 'Activity' &&
    relatedEntityId
  ) {
    return {
      context: 'activity',
      itemId: relatedEntityId,
      taskId: entityId,
      taskType: 'cc',
    };
  }

  // Direct entity type to context mappings
  const contextMap: Record<string, MapContext> = {
    Activity: 'activity',
    Milestone: 'milestone',
    Phase: 'phase',
    Workstream: 'workstream',
  };

  const context = contextMap[entityType];
  if (context) {
    return { context, itemId: entityId };
  }

  // Unsupported entity types
  return { context: null, itemId: entityId };
}

/**
 * Navigate to an entity by updating the map sidebar and router.
 *
 * Opens the appropriate map sidebar for the entity and updates URL query params for deep linking.
 */
export async function navigateToEntity(
  group: EntityGroup,
  router: Router,
  route: RouteLocationNormalizedLoaded,
  mapStore: ReturnType<typeof useMapStore>,
): Promise<void> {
  // Resolve navigation context for this entity
  const { context, itemId, taskId, taskType } = resolveEntityNavigationContext(
    group.entityType,
    group.entityId,
    null,
    null,
  );

  // Skip unsupported entity types
  if (!context) {
    console.warn('No sidebar component for entity type:', group.entityType);

    return;
  }

  // Update map store to show sidebar
  await mapStore.mapSelectItem(context, itemId);

  // Set task selection for child entities (Actions/CC)
  if (taskId && taskType) {
    mapStore.selectedTaskId = taskId;
    mapStore.selectedTaskType = taskType;
  }

  // Optional: Update router query params for deep linking
  if (context === 'activity' && itemId) {
    const routeQuery = { ...route.query };
    delete routeQuery.selectedActionId;
    delete routeQuery.selectedCompletionCriterionId;

    await router.replace({
      path: route.path,
      query: { ...routeQuery, selectedActivityId: itemId },
    });
  } else if (context === 'milestone' && itemId) {
    await router.replace({
      path: route.path,
      query: { ...route.query, selectedMilestoneId: itemId },
    });
  }
  // Add similar patterns for other entity types if needed
}
